/**
 * Reviewed updates and the concurrency check that gates them.
 *
 * A review suggestion carries the `updated_at` it was computed from; applying
 * it is refused when the live profile has moved on. Imported by the LLM output
 * schema that the frontend also compiles, so this module must stay free of
 * database, logger, and other server-only imports — the SQL half of the same
 * check lives in `db.ts`.
 */

// JS Date and `toISOString()` are millisecond-precise; Postgres timestamptz is
// not. Every staleness check (apply, validator, GraphQL `is_stale`) must use
// this same millisecond comparison.
export function brandProfileTimestampsMatch(
  liveUpdatedAt: Date | string,
  reviewedUpdatedAt: string,
): boolean {
  return (
    new Date(liveUpdatedAt).getTime() === new Date(reviewedUpdatedAt).getTime()
  );
}

// Date#toString() is not a timestamptz; the is_stale SQL casts this with ::timestamptz.
const BRAND_PROFILE_REVIEWED_UPDATED_AT_ISO =
  /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{1,9})?(?:Z|[+-]\d{2}:\d{2})$/;

export function isBrandProfileReviewedUpdatedAtIso(value: string): boolean {
  return (
    BRAND_PROFILE_REVIEWED_UPDATED_AT_ISO.test(value) &&
    !Number.isNaN(Date.parse(value))
  );
}

/**
 * True when the suggestion can no longer be applied: it names a profile that
 * is gone or different, or it carries no token, or the token has been
 * superseded.
 */
export function isBrandProfileUpdateStale(args: {
  live: { id: string; updatedAt: Date | string } | null;
  profileId?: string | null;
  reviewedUpdatedAt?: string | null;
}): boolean {
  if (!args.live) {
    return args.profileId != null || args.reviewedUpdatedAt != null;
  }
  if (args.profileId && args.profileId !== args.live.id) return true;
  if (!args.reviewedUpdatedAt) return true;
  return !brandProfileTimestampsMatch(
    args.live.updatedAt,
    args.reviewedUpdatedAt,
  );
}
